/**
 * SearchInput — Dual-mode search component.
 *
 * mode="search" → Database search. Commits on Enter or button click.
 *                  Sends query to the server for full-database search.
 *
 * mode="find"   → Client-side filter. Updates instantly on every keystroke.
 *                  Filters results already loaded in memory.
 *
 *                  When `tokens` / `onTokensChange` are supplied, find mode
 *                  switches to a tokenized chip-input: pressing Enter
 *                  commits the current input as a saved term (rendered as
 *                  a removable badge), letting users build multi-word terms
 *                  that contain spaces without ambiguous whitespace splitting.
 *
 * See docs/SUPABASE_1000_RECORD_LIMIT.md for why both modes exist.
 */

import { useRef, forwardRef, useImperativeHandle } from 'react';

interface SearchInputProps {
  mode: 'search' | 'find';
  value: string;
  onChange: (value: string) => void;
  /** Called when user commits a search (Enter key or button click). Only used in "search" mode. */
  onSearch?: (value: string) => void;
  placeholder?: string;
  isSearching?: boolean;
  resultCount?: number;
  className?: string;
  /**
   * Committed search terms rendered as badges before the input. Pass
   * together with `onTokensChange` to enable tokenized chip behavior
   * (find mode only). Each Enter commits the typed text as a new token;
   * Backspace on an empty input removes the last token.
   */
  tokens?: string[];
  onTokensChange?: (tokens: string[]) => void;
  /**
   * Escalation hook for find mode: when supplied, a "Search All DB"
   * button appears next to the Clear control whenever the user has any
   * find content (tokens or in-progress input). Clicking it invokes
   * this callback with the combined query (tokens + current input,
   * space-joined) so the parent can re-run the same terms against the
   * full database instead of the currently loaded page. Parent decides
   * what to do with the find state (typically: clear it).
   */
  onEscalateToDbSearch?: (combinedQuery: string) => void;
}

export interface SearchInputHandle {
  focus: () => void;
  clear: () => void;
}

const SearchInput = forwardRef<SearchInputHandle, SearchInputProps>(
  ({ mode, value, onChange, onSearch, placeholder, isSearching, resultCount, className = '', tokens, onTokensChange, onEscalateToDbSearch }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const tokenized = mode === 'find' && Array.isArray(tokens) && typeof onTokensChange === 'function';

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      clear: () => {
        onChange('');
        if (mode === 'search' && onSearch) onSearch('');
        if (tokenized) onTokensChange!([]);
      },
    }));

    const commitToken = () => {
      if (!tokenized) return;
      const trimmed = value.trim();
      if (!trimmed) return;
      // Dedupe case-insensitively — committing "West" when "west" is
      // already present is almost always a typo, not an intentional
      // additional term.
      const exists = tokens!.some((t) => t.toLowerCase() === trimmed.toLowerCase());
      if (!exists) onTokensChange!([...tokens!, trimmed]);
      onChange('');
    };

    const removeTokenAt = (idx: number) => {
      if (!tokenized) return;
      onTokensChange!(tokens!.filter((_, i) => i !== idx));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (mode === 'search' && e.key === 'Enter' && onSearch) {
        onSearch(value);
        return;
      }
      if (tokenized) {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitToken();
          return;
        }
        // Backspace on an empty input removes the last committed token —
        // standard chip-input convention.
        if (e.key === 'Backspace' && value === '' && tokens!.length > 0) {
          e.preventDefault();
          removeTokenAt(tokens!.length - 1);
        }
      }
    };

    const isFind = mode === 'find';

    // Combined query passed to the parent's escalation callback. We
    // include both committed tokens (tokenized find) and the in-progress
    // input so the user doesn't have to commit a token first — if they
    // typed something and want to escalate immediately, that text comes
    // along too.
    const buildEscalationQuery = () => {
      const live = value.trim();
      if (tokenized) {
        return [...(tokens || []), live].filter(Boolean).join(' ');
      }
      return live;
    };
    const hasFindContent = isFind && (Boolean(value.trim()) || (tokenized && (tokens?.length ?? 0) > 0));
    const showEscalationButton = hasFindContent && typeof onEscalateToDbSearch === 'function';
    const escalationButton = showEscalationButton ? (
      <button
        type="button"
        onClick={() => {
          const q = buildEscalationQuery();
          if (!q) return;
          onEscalateToDbSearch!(q);
        }}
        title="Don't see what you want? Re-run these terms against the entire database"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-hover rounded-lg whitespace-nowrap transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM3.6 9h16.8M3.6 15h16.8M11.5 3a17 17 0 000 18M12.5 3a17 17 0 010 18" />
        </svg>
        Search All DB
      </button>
    ) : null;

    const ringColor = isFind ? 'focus:ring-yellow-400 focus:border-yellow-400' : 'focus:ring-primary focus:border-primary';
    const iconColor = isFind ? 'text-yellow-500' : 'text-gray-400';

    const wrapperFocusRing = tokenized
      ? 'focus-within:ring-2 focus-within:ring-yellow-400 focus-within:border-yellow-400'
      : '';

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="relative flex-1">
          <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${iconColor}`}>
            {isFind ? (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 010 2H4a1 1 0 01-1-1zm3 6a1 1 0 011-1h10a1 1 0 010 2H7a1 1 0 01-1-1zm2 6a1 1 0 011-1h6a1 1 0 010 2H9a1 1 0 01-1-1z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </div>
          {tokenized ? (
            <div
              className={`flex flex-wrap items-center gap-1 w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus-within:outline-none ${wrapperFocusRing}`}
              onClick={() => inputRef.current?.focus()}
            >
              {tokens!.map((token, idx) => (
                <span
                  key={`${token}-${idx}`}
                  className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-900 text-xs px-2 py-0.5 rounded-full border border-yellow-300"
                >
                  <span className="max-w-[16rem] truncate">{token}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${token}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTokenAt(idx);
                    }}
                    className="text-yellow-700 hover:text-yellow-900 leading-none"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={commitToken}
                placeholder={tokens!.length === 0 ? (placeholder || 'Filter current results... (press Enter to add)') : ''}
                className="flex-1 min-w-[8rem] bg-transparent border-0 outline-none focus:ring-0 text-sm py-1"
              />
            </div>
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || (isFind ? 'Filter current results...' : 'Search entire database... (press Enter)')}
              className={`block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white ${ringColor} focus:outline-none focus:ring-2 ${className}`}
            />
          )}
        </div>

        {mode === 'search' && (
          <>
            <button
              onClick={() => onSearch?.(value)}
              disabled={isSearching}
              className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            {value && (
              <button
                onClick={() => { onChange(''); onSearch?.(''); }}
                className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear
              </button>
            )}
          </>
        )}

        {mode === 'find' && !tokenized && value && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 bg-yellow-100 px-2 py-1 rounded whitespace-nowrap">
              Highlighting matches
            </span>
            {escalationButton}
            <button
              onClick={() => onChange('')}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Clear
            </button>
          </div>
        )}

        {tokenized && (tokens!.length > 0 || value) && (
          <>
            {escalationButton}
            <button
              onClick={() => {
                onChange('');
                onTokensChange!([]);
              }}
              className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
            >
              Clear all
            </button>
          </>
        )}

        {mode === 'search' && resultCount !== undefined && value && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {resultCount.toLocaleString()} result{resultCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
export default SearchInput;
